import axios, { AxiosResponse } from "axios";
import apiService from "./apiService";
import { OptionsTable, QueryResult } from "@/models/table";
import logger from "@/services/loggerService";
import {
  PolicyDeserializer,
  PolicySearchSerializer,
  PolicyFieldsDeserializer,
  Policy,
  PolicySerializer,
  PoliciesGroups,
  groupsDecoder,
  possibleGroups,
  FieldValues,
  PolicyField,
} from "@/models/policy";

export default {
  async search(
    options: OptionsTable,
    searchString?: string,
    groupFilter = possibleGroups[0] as PoliciesGroups
  ): Promise<QueryResult> {
    // eslint-disable-next-line @typescript-eslint/ban-ts-comment
    // @ts-ignore
    const groupId = groupsDecoder[groupFilter];
    const customUrl = `${apiService.policy.searchUrl()}/${groupId}`;
    const data = PolicySearchSerializer(options, searchString);
    const res: AxiosResponse = await axios.post(customUrl, data);
    return {
      items: res.data.items,
      count: res.data.count,
    } as QueryResult;
  },
  async get(id: string): Promise<Policy | undefined> {
    //TODO MISSING API METHOD - temporary getting from list NEEDS MAJOR REFACTOR
    const data = {
      limit: 1000,
      page: 1,
      sortBy: [],
      filterBy: [],
    };
    const res: AxiosResponse = await axios.post(
      apiService.policy.searchUrl(),
      data
    );
    const rawpolicy = res.data.items.find((policy: any) => {
      return policy.policyId === id;
    });
    const policy = PolicyDeserializer(rawpolicy);
    try {
      const res = await axios.get(
        apiService.policy.fieldsUrl().replace(":id", id)
      );
      policy.fields = PolicyFieldsDeserializer(res.data);
      this.adaptPolicyFields(policy);
      return policy;
      // return PolicyDetailedDeserializer(res.data, policy);
    } catch (e) {
      logger.error(e);
    }
  },
  async groups(): Promise<Array<Record<string, string | number>>> {
    const res = await axios.get(apiService.policy.groupsUrl());
    return res.data;
  },
  async post(policy: Policy): Promise<string> {
    const res = await axios.post(
      apiService.policy.baseUrl(),
      PolicySerializer(policy)
    );
    return res.data;
  },
  async put(policy: Policy): Promise<string> {
    const res = await axios.put(
      apiService.policy.baseUrl() + "/" + policy.id,
      PolicySerializer(policy)
    );
    return res.data;
  },
  async applyToGroup(groupId: string): Promise<string> {
    const res = await axios.post(
      apiService.group.baseUrl() + "/" + groupId + "/apply-policies"
    );
    return res.data;
  },
  async delete(id: string): Promise<AxiosResponse> {
    return await axios.delete(apiService.policy.baseUrl() + "/" + id);
  },
  async fields(policyId: string): Promise<Array<any>> {
    const url = apiService.policy.fieldsUrl().replace(":id", policyId);
    const res = await axios.get(url);
    return res.data;
  },
  validateFileFields(policy: Policy): string | undefined {
    //HACK custom validation for some policies with files
    if (policy.fields) {
      if (
        policy.fields.CustomLogoActive?.keyValue == true &&
        !policy.fields.Logo?.keyValue
      ) {
        return "Logo field is required";
      }
      if (
        policy.fields.CustomBackgroundActive?.keyValue == true &&
        !policy.fields.Backgrounds?.keyValue
      ) {
        return "Background field is required";
      }
      if (
        policy.fields.ShowCustomBackground?.keyValue == true &&
        !policy.fields.WorkspaceBackground?.keyValue
      ) {
        return "Custom background field is required";
      }
    }
  },
  async getDefaults(groupId: string): Promise<{ [key: string]: PolicyField }> {
    const defaultRes = await axios.get(
      apiService.policy.defaultsUrl().replace(":id", groupId)
    );
    return PolicyFieldsDeserializer(defaultRes.data);
  },
  async extractFields(fields: {
    [key: string]: PolicyField;
  }): Promise<{ [key: string]: FieldValues }> {
    const res = {} as any;
    Object.keys(fields).forEach(function (key) {
      if (fields) {
        res[key] = fields[key].keyValue;
      }
    });
    return res;
  },
  adaptPolicyFields(policy: Policy): Policy {
    //HACK force business logic operations constraints for fetched policies
    if (policy.fields) {
      if (
        policy.type == "advanced" &&
        policy.fields.CustomEmailService == undefined
      ) {
        policy.fields.CustomEmailService = {
          keyName: "CustomEmailService",
          keyValue: policy.fields.UseSmtpToSendMail.keyValue ? 1 : 0,
          keyType: "number",
        };
      }
      if (
        policy.type == "advanced" &&
        policy.fields.UseSmtpToSendMail == undefined
      ) {
        policy.fields.UseSmtpToSendMail = {
          keyName: "UseSmtpToSendMail",
          keyValue: policy.fields.CustomEmailService.keyValue > 0,
          keyType: "boolean",
        };
      }
      if (
        (policy.type == "appearance" && !policy.fields.HomeAction1) ||
        !policy.fields.HomeAction1.keyValue == null
      ) {
        policy.fields.HomeAction1 = {
          keyName: "HomeAction1",
          keyValue: "Conference",
          keyType: "string",
        };
      }
      if (
        (policy.type == "appearance" && !policy.fields.HomeAction2) ||
        !policy.fields.HomeAction2.keyValue == null
      ) {
        policy.fields.HomeAction2 = {
          keyName: "HomeAction2",
          keyValue: "Whiteboard",
          keyType: "string",
        };
      }
      if (
        (policy.type == "appearance" && !policy.fields.HomeAction3) ||
        !policy.fields.HomeAction3.keyValue == null
      ) {
        policy.fields.HomeAction3 = {
          keyName: "HomeAction3",
          keyValue: "HowTo",
          keyType: "string",
        };
      }
      if (
        (policy.type == "appearance" && !policy.fields.HomeAction4) ||
        !policy.fields.HomeAction4.keyValue == null
      ) {
        policy.fields.HomeAction4 = {
          keyName: "HomeAction4",
          keyValue: "Apps",
          keyType: "string",
        };
      }
      if (
        policy.fields.HomeAction4 &&
        (policy.fields.HomeAction4 == policy.fields.HomeAction3 ||
          policy.fields.HomeAction4 == policy.fields.HomeAction2 ||
          policy.fields.HomeAction4 == policy.fields.HomeAction1)
      ) {
        policy.fields.HomeAction4 = {
          keyName: "HomeAction4",
          keyValue: "None",
          keyType: "string",
        };
      }
      if (
        policy.fields.HomeAction3 &&
        (policy.fields.HomeAction3 == policy.fields.HomeAction2 ||
          policy.fields.HomeAction3 == policy.fields.HomeAction1)
      ) {
        policy.fields.HomeAction3 = {
          keyName: "HomeAction3",
          keyValue: "None",
          keyType: "string",
        };
      }
      if (
        policy.fields.HomeAction2 &&
        policy.fields.HomeAction2 == policy.fields.HomeAction1
      ) {
        policy.fields.HomeAction2 = {
          keyName: "HomeAction2",
          keyValue: "None",
          keyType: "string",
        };
      }
    }
    return policy;
  },
  clearPolicyFields(policy: Policy): Policy {
    //HACK enforce some business logic constraints before saving policy
    if (policy.fields) {
      if (
        policy.type == "appearance" &&
        !policy.fields.VoiceCommandsActive?.keyValue
      ) {
        policy.fields.VoiceCommandsCultureKey = {
          keyName: "VoiceCommandsCultureKey",
          keyValue: "en-US",
          keyType: "string",
        };
        policy.fields.DisableVoiceSuggestion = {
          keyName: "DisableVoiceSuggestion",
          keyValue: false,
          keyType: "boolean",
        };
      }
      if (
        policy.type == "calendar" &&
        policy.fields.DefaultExtendCountdownAction?.keyValue != "0"
      ) {
        policy.fields.NotifyEndMeetingTimeout = {
          keyName: "NotifyEndMeetingTimeout",
          keyValue: 30,
          keyType: "int",
        };
      }
      if (policy.type == "calendar") {
        policy.fields.NotifyEndMeetingTimeoutEnabled = {
          keyName: "NotifyEndMeetingTimeoutEnabled",
          keyValue: true,
          keyType: "int",
        };
      }
      if (
        policy.type == "presentation" &&
        policy.fields.WiredHdmiWithThirdPartyMode?.keyValue !== "1"
      ) {
        policy.fields.WiredHdmiWithThirdPartyMode = {
          keyName: "WiredHdmiWithThirdPartyMode",
          keyValue: "0",
          keyType: "string",
        };
      }
      if (policy.fields.CustomLogoActive?.keyValue == false) {
        policy.fields.Logo = {
          keyName: "Logo",
          keyValue: "",
          keyType: "file",
        };
      }
      if (policy.fields.CustomBackgroundActive?.keyValue == false) {
        policy.fields.Backgrounds = {
          keyName: "Backgrounds",
          keyValue: "",
          keyType: "file",
        };
      }
      if (policy.fields.DigitalSignageActive?.keyValue == false) {
        policy.fields.WebUrlBackground = {
          keyName: "WebUrlBackground",
          keyValue: "",
          keyType: "string",
        };
      }
      if (policy.fields.PeopleCountEnabled?.keyValue == false) {
        // PeopleCountEnabled
        // PeopleCountOnScreenNotification
        policy.fields.PeopleCountRoomCapacity.keyValue = 0;
        policy.fields.PeopleCountOnScreenNotification.keyValue = false;
      }

      if (
        policy.fields.PeopleCountEnabled?.keyValue == true &&
        policy.fields.PeopleCountRoomCapacity.keyValue == 0
      ) {
        policy.fields.PeopleCountRoomCapacity.keyValue = 1;
      }

      if (policy.fields.ShowWifiData?.keyValue == false) {
        policy.fields.WifiSSID = {
          keyName: "WifiSSID",
          keyValue: "",
          keyType: "string",
        };
        policy.fields.WifiPassword = {
          keyName: "WifiPassword",
          keyValue: "",
          keyType: "string",
        };
      }
      if (policy.fields.CrestronEnabled?.keyValue == false) {
        policy.fields.CrestronUrl = {
          keyName: "CrestronUrl",
          keyValue: "",
          keyType: "string",
        };
        policy.fields.CrestronUsername = {
          keyName: "CrestronUsername",
          keyValue: "",
          keyType: "string",
        };
        policy.fields.CrestronPassword = {
          keyName: "CrestronPassword",
          keyValue: "",
          keyType: "string",
        };
      }
      if (policy.fields.ShowCustomBackground?.keyValue == false) {
        policy.fields.WorkspaceBackground = {
          keyName: "WorkspaceBackground",
          keyValue: "",
          keyType: "string",
        };
      }
      if (policy.fields.IsHealthCheckEnabled?.keyValue == false) {
        policy.fields.HealthCheckSpeakerMute = {
          keyName: "HealthCheckSpeakerMute",
          keyValue: false,
          keyType: "boolean",
        };
        policy.fields.HealthCheckSpeakerLowLevel = {
          keyName: "HealthCheckSpeakerLowLevel",
          keyValue: false,
          keyType: "boolean",
        };
        policy.fields.HealthCheckMicrophoneMute = {
          keyName: "HealthCheckMicrophoneMute",
          keyValue: false,
          keyType: "boolean",
        };
        policy.fields.HealthCheckMicrophoneLowLevel = {
          keyName: "HealthCheckMicrophoneLowLevel",
          keyValue: false,
          keyType: "boolean",
        };
        policy.fields.HealthCheckHasCamera = {
          keyName: "HealthCheckHasCamera",
          keyValue: false,
          keyType: "boolean",
        };
        policy.fields.HealthCheckHasInternetConnection = {
          keyName: "HealthCheckHasInternetConnection",
          keyValue: false,
          keyType: "boolean",
        };
        policy.fields.HealthCheckHasValareaConnection = {
          keyName: "HealthCheckHasValareaConnection",
          keyValue: false,
          keyType: "boolean",
        };
        policy.fields.SmtpSendHealthCheck = {
          keyName: "SmtpSendHealthCheck",
          keyValue: false,
          keyType: "boolean",
        };
      }
      if (policy.fields.SmtpSendHealthCheck?.keyValue == false) {
        policy.fields.SmtpSendHealthCheck = {
          keyName: "SmtpSendHealthCheckReceiver",
          keyValue: "",
          keyType: "string",
        };
        policy.fields.SmtpSendHealthCheckTime = {
          keyName: "SmtpSendHealthCheckTime",
          keyValue: 15,
          keyType: "int",
        };
      }
      if (policy.fields.RMSServerUrlToggle?.keyValue == false) {
        policy.fields.RMSServerUrl = {
          keyName: "RMSServerUrl",
          keyValue: "",
          keyType: "string",
        };
      }
      if (policy.fields.EnableRebootTask?.keyValue == false) {
        policy.fields.RebootTime = {
          keyName: "RebootTime",
          keyValue: "12:00 AM",
          keyType: "string",
        };
      }
    }
    return policy;
  },
};
